<script setup lang="ts">
import type { Game, GameClickEventEmit, GameListProps, GameTilePosition } from '@game/types'
import { vIntersectionObserver } from '@vueuse/components'
import { computed, nextTick, onMounted, ref, watch, useDevice, useGameImpressions, useImpressionObserver, useResizeObserver, useGameAssets, useDebounceFn, useI18n, useNuxtApp, useRoute, useTileVisibility, useTracking, useUserInfo } from '#imports'

const { isVideoTile } = useGameAssets()
const { t } = useI18n()
// User-agent based so SSR and client agree (eager set is part of SSR markup)
const { isMobile } = useDevice()

const props = withDefaults(defineProps<GameListProps>(), {
    showAllButton: false,
    forceShowAllButton: false,
    showNewGames: true,
    hasSidebar: false,
    videoEnabled: true,
    skeletons: 5,
})

const emit = defineEmits<GameClickEventEmit>()

const { trackEvent } = useTracking()

const rowCount = computed(() => (props.inlineRows && props.inlineRows > 1 ? props.inlineRows : 1))

// Games flow column-major via CSS grid-auto-flow:column — game at flat index `i`
// lands in row `i % rows` / column `floor(i / rows)`.
function tilePosition(flatIndex: number): GameTilePosition {
    const row = flatIndex % rowCount.value
    const col = Math.floor(flatIndex / rowCount.value)
    return { row: row + 1, col: col + 1, index: flatIndex }
}

const skeletonRows = computed(() => props.inlineRows || 1)

const gameListElement = ref<HTMLDivElement>()
const disablePrev = ref(true)
const disableNext = ref(true)

const GAP = 10

const scrollGameList = (next = true) => {
    if (!gameListElement.value) return

    const el = gameListElement.value
    const tileWidth = el.querySelector('.game-tile')?.clientWidth
    if (!tileWidth) return

    const { scrollWidth, clientWidth, scrollLeft } = el
    const stride = tileWidth + GAP
    const scrollMax = scrollWidth - clientWidth

    // The prev/next overlays fade the tiles beneath them. Align each scroll so the
    // game half-hidden under the leading overlay becomes the first fully visible
    // one, derived from the tile actually cut off now — this self-corrects for the
    // start, where only one overlay is shown, without a fixed page size.
    const overlay = el.parentElement?.querySelector<HTMLElement>('.game-list__next')?.clientWidth ?? 0

    let target
    if (next) {
        const firstRightCut = Math.floor((scrollLeft + clientWidth - overlay - tileWidth) / stride) + 1
        target = firstRightCut * stride - overlay
    }
    else {
        const firstLeftCut = Math.ceil((scrollLeft + overlay) / stride) - 1
        target = firstLeftCut * stride + tileWidth - clientWidth + overlay
    }
    target = Math.max(0, Math.min(scrollMax, target))

    el.scrollTo({ left: target, behavior: 'smooth' })

    disableNext.value = target >= scrollMax
    disablePrev.value = target <= 0
}

if (import.meta.client) {
    useResizeObserver(gameListElement, () => {
        if (!gameListElement.value) return

        const { scrollWidth, clientWidth, scrollLeft } = gameListElement.value
        const scrollMax = scrollWidth - clientWidth

        disableNext.value = scrollMax <= 0 || scrollLeft >= scrollMax
        disablePrev.value = scrollLeft <= 0
    })
}

const { mounted: loadedTiles, active: mediaTiles, setVisible: setTileVisible, onTileVisible, observerOptions } = useTileVisibility()

// Eager only during SSR + hydration so SSR/client markup agree (no hydration
// mismatch); client-side navs after hydration lazy-load via IntersectionObserver.
const nuxtApp = useNuxtApp()
const eagerTiles = computed(() => (import.meta.server || nuxtApp.isHydrating) && !!props.games?.length)

const eagerTileCount = computed(() => props.eagerTileCount ?? (isMobile ? 3 : 8) * Math.max(1, props.inlineRows ?? 1))

// Slugs of the first eager tiles, by game order — O(1) lookup per tile instead
// of a findIndex scan on every render.
const eagerSlugs = computed<Set<string>>(() => {
    if (!eagerTiles.value) return new Set()

    return new Set((props.games ?? []).slice(0, eagerTileCount.value).map(game => game.slug))
})

function isEagerTile(slug: string) {
    return eagerSlugs.value.has(slug)
}

watch(eagerSlugs, (slugs) => {
    for (const slug of slugs) {
        setTileVisible(slug, true)
    }
}, { immediate: true })

const { data: userInfo } = useUserInfo()
const impressions = useGameImpressions()

const trackingUrl = useRoute().path

const slugToIndex = computed(() => new Map((props.games ?? []).map((game, index) => [game.slug, index])))

const { observe: observeImpressions } = useImpressionObserver('[data-tile-slug]', (el) => {
    const slug = el.dataset.tileSlug
    const index = slug ? slugToIndex.value.get(slug) : undefined
    if (!slug || index === undefined) return

    impressions.record({
        origin: props.trackingOrigin,
        listSlug: props.trackingListSlug ?? props.category,
        gameSlug: slug,
        url: trackingUrl,
        ...tilePosition(index),
        userId: userInfo.value?.id,
    })
})

onMounted(() => {
    if (impressions.enabled) observeImpressions(gameListElement.value)
})

if (import.meta.client) {
    watch(() => props.games, async () => {
        await nextTick()

        if (impressions.enabled) observeImpressions(gameListElement.value)
    })
}

const listHasBeenScrolled = ref(false)

const handleScrollTracking = useDebounceFn((event: Event) => {
    const target = event.target as HTMLDivElement
    const { scrollWidth, clientWidth, scrollLeft } = target

    if (!listHasBeenScrolled.value) {
        void trackEvent({
            event_name: `gamelist_scrolled`,
            event_type: 'Scroll',
            meta_data: [{
                category: 'Games',
                gamesCategory: props.category,
            }],
            log_entry: true
        })

        listHasBeenScrolled.value = true
    }

    if (scrollLeft + clientWidth >= scrollWidth) {
        void trackEvent({
            event_name: `gamelist_scrolled-to-end`,
            event_type: 'Scroll',
            meta_data: [{
                category: 'Games',
                gamesCategory: props.category,
                lastRow: !!target.previousElementSibling
            }],
            log_entry: true
        })
    }
}, 200, { maxWait: 1000 })
</script>

<template>
    <div class="game-list__container">
        <template v-if="props.games?.length">
            <div
                ref="gameListElement"
                class="game-list"
                :style="{
                    '--inline-rows': rowCount,
                    '--fade-start': disablePrev ? '0px' : 'var(--fade-width)',
                    '--fade-end': disableNext ? '0px' : 'var(--fade-width)',
                }"
                @scroll.passive="handleScrollTracking"
            >
                <div class="game-list__rows">
                    <div
                        v-for="(game, gameIndex) in props.games"
                        :key="`${category}_${game.slug}`"
                        v-intersection-observer="[onTileVisible, observerOptions]"
                        :data-tile-slug="game.slug"
                        class="game-list__tile-slot"
                        :class="{ 'game-list__tile-slot--video': isVideoTile(game, props.videoEnabled) }"
                    >
                        <GameTile
                            v-if="loadedTiles.has(game.slug)"
                            :game="game"
                            :video-enabled="props.videoEnabled"
                            :is-inline="true"
                            :is-mini="props.miniTiles"
                            :eager="isEagerTile(game.slug)"
                            :load-media="mediaTiles.has(game.slug)"
                            :tile-variant="props.tileVariant"
                            :style="{ '--game-index': gameIndex }"
                            @game-click="(game: Game) => emit('game-click', game, tilePosition(gameIndex))"
                        />

                        <div v-else class="skeleton-item" />
                    </div>
                </div>
            </div>
        </template>
        <template v-else-if="skeletonRows">
            <div class="game-list" :style="{ '--inline-rows': skeletonRows }">
                <div class="game-list__rows">
                    <div v-for="i in skeletons * skeletonRows" :key="`skeleton_${i}`" class="skeleton-item" />
                </div>
            </div>
        </template>
        <div
            class="game-list__prev"
            :class="{ 'game-list__prev--disabled': disablePrev }"
        >
            <button :disabled="disablePrev" :aria-label="t('game.gameList.scroll_previous')" @click.prevent="scrollGameList(false)">
                <UIIcon name="arrow-left" size="9" />
            </button>
        </div>
        <div
            class="game-list__next"
            :class="{ 'game-list__next--disabled': disableNext }"
        >
            <button :disabled="disableNext" :aria-label="t('game.gameList.scroll_next')" @click.prevent="scrollGameList(true)">
                <UIIcon name="arrow-right" size="9" />
            </button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
$controlBtnBg: $game-load-more-bg;
$controlBtnColor: $game-load-more-color;
$controlBtnBgHover: $game-load-more-bg-hover;
$controlBtnColorHover: $game-load-more-color-hover;

$edge-fade: linear-gradient(
    to right,
    transparent 0,
    #000 var(--fade-start, 0px),
    #000 calc(100% - var(--fade-end, 0px)),
    transparent 100%
);

.game-list {
    position: relative;

    -webkit-mask-image: $edge-fade;
    mask-image: $edge-fade;

    &__container {
        display: grid;
        gap: 10px;

        --fade-width: 80px;

        @include tablet {
            --fade-width: 100px;
        }

        @include desktop {
            --fade-width: 120px;
        }
    }

    &__prev,
    &__next {
        position: absolute;
        width: var(--fade-width);
        height: 100%;
        bottom: 0;
        left: 0;

        z-index: 6;
        pointer-events: none;

        button {
            position: absolute;
            display: none;
            width: 32px;
            height: 50px;
            left: 0;
            bottom: 50%;
            margin: 0;
            border: none;
            padding: 0;
            transform: translateY(50%);

            align-items: center;
            justify-content: center;
            border-radius: var(--border-radius, 8px);
            color: $controlBtnColor;
            transition: all 0.4s $easeOutQuart;
            background: $controlBtnBg;
            opacity: 0.8;
            pointer-events: all;

            @include tablet {
                width: 40px;
                display: flex;
            }

            &:disabled {
                pointer-events: none;
                opacity: 0;
            }

            &:not([disabled]):hover {
                color: $controlBtnColorHover;
                background: $controlBtnBgHover;
                opacity: 1;
            }
        }

        &--disabled {
            opacity: 0;
        }
    }

    &__next {
        left: auto;
        right: 0;

        button {
            left: auto;
            right: 15px;

            @include desktop {
                right: 0;
            }
        }
    }
}
</style>
